import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { makeStyles, tokens, Text, TabList, Tab } from '@coreai/fluentui-react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '@/stores/appStore';
import { useWorkflowStore, type SelectedNode } from '@/stores/workflowStore';
import { serializeWorkflow } from '@/services/workflowSerializer';
import { useCodeMirror } from './useCodeMirror';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { useDraftModeStore, selectIsReadOnly } from '@/stores/draftModeStore';
import type { ActiveFile, NodeHighlightRange } from './types';

/**
 * Find the character ranges of a node (trigger or action) in the workflow JSON.
 * Returns separate ranges for the node name and the entire block.
 */
const findNodeRange = (
  json: string,
  selectedNode: SelectedNode
): NodeHighlightRange | null => {
  try {
    // Find the appropriate section (triggers or actions)
    const section = selectedNode.type === 'trigger' ? '"triggers"' : '"actions"';
    const sectionIndex = json.indexOf(section);
    if (sectionIndex === -1) return null;

    // Find the node name key within the section
    // The node name in JSON will be like "nodeName": {
    const searchKey = `"${selectedNode.name}"`;
    const searchStartIndex = sectionIndex;

    // Find the node key after the section
    const keyIndex = json.indexOf(searchKey, searchStartIndex);
    if (keyIndex === -1) return null;

    // Make sure we're finding it in the right section by checking we haven't
    // crossed into another top-level section
    const nextSectionPattern = /^\s*"(triggers|actions|parameters|staticResults)"/gm;
    nextSectionPattern.lastIndex = sectionIndex + section.length;
    const nextMatch = nextSectionPattern.exec(json);
    if (nextMatch && keyIndex > nextMatch.index) {
      // The found key is in a different section, search again
      return null;
    }

    // The name range is the key (including quotes)
    const nameFrom = keyIndex;
    const nameTo = keyIndex + searchKey.length;

    // Find the opening brace of the node object
    const colonIndex = json.indexOf(':', keyIndex);
    if (colonIndex === -1) return null;

    // Find the opening brace after the colon
    let braceIndex = colonIndex + 1;
    while (braceIndex < json.length && json[braceIndex] !== '{') {
      braceIndex++;
    }
    if (braceIndex >= json.length) return null;

    // Now find the matching closing brace
    let depth = 1;
    let endIndex = braceIndex + 1;
    while (endIndex < json.length && depth > 0) {
      const char = json[endIndex];
      if (char === '{') depth++;
      else if (char === '}') depth--;
      endIndex++;
    }

    // Return the ranges
    return {
      nameFrom,
      nameTo,
      blockFrom: keyIndex,
      blockTo: endIndex,
    };
  } catch {
    return null;
  }
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: '36px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  fileTabs: {
    minWidth: 0,
  },
  errorBadge: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  readOnlyBadge: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  editorContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  editorWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    '& .cm-editor': {
      height: '100%',
      overflow: 'hidden',
    },
    // Apply custom scrollbar styles to CodeMirror's scroller
    '& .cm-scroller': {
      ...customScrollbarStyles,
    },
  },
});

type CodeEditorProps = {
  hideHeader?: boolean;
  /**
   * When provided, overrides the internal draft-mode read-only signal. Use
   * this to fold in additional read-only signals (e.g. permission-read-only)
   * so CodeMirror actually blocks edits a user cannot persist. Defaults to
   * the draft-mode signal only, preserving pre-existing behavior for
   * callers that don't opt in.
   */
  readOnly?: boolean;
};

export const CodeEditor = ({ hideHeader = false, readOnly: readOnlyOverride }: CodeEditorProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);
  // Read draft-mode read-only state directly from the store so this works
  // regardless of where the DraftModeProvider lives in the tree.
  const draftReadOnly = useDraftModeStore(selectIsReadOnly);
  const isReadOnly = readOnlyOverride ?? draftReadOnly;
  const {
    activeFile,
    setActiveFile,
    workflow,
    workflowJson,
    connectionsJson,
    connections,
    updateWorkflowFromJson,
    updateConnectionsFromJson,
    setCodeBufferState,
    selectedNode,
  } = useWorkflowStore(
    useShallow((s) => ({
      activeFile: s.activeFile,
      setActiveFile: s.setActiveFile,
      workflow: s.workflow,
      workflowJson: s.workflowJson,
      connectionsJson: s.connectionsJson,
      connections: s.connections,
      updateWorkflowFromJson: s.updateWorkflowFromJson,
      updateConnectionsFromJson: s.updateConnectionsFromJson,
      setCodeBufferState: s.setCodeBufferState,
      selectedNode: s.selectedNode,
    }))
  );

  const handleFileTabChange = (_: unknown, data: { value: unknown }) => {
    setActiveFile(data.value as ActiveFile);
  };

  const [parseError, setParseError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousActiveFileRef = useRef<ActiveFile>(activeFile);
  const previousContentRef = useRef<string>('');

  // Get current content based on active file.
  // For workflow.json, run through the serializer to strip internal-only
  // properties (e.g., detached) so the code view shows what the runtime receives.
  const currentContent = useMemo(() => {
    if (activeFile !== 'workflow.json') return connectionsJson;
    if (!workflow) return workflowJson;
    try {
      return serializeWorkflow(workflow, { pretty: true });
    } catch {
      // Fallback to raw JSON if serializer fails (shouldn't happen)
      return workflowJson;
    }
  }, [activeFile, workflow, workflowJson, connectionsJson]);

  // Ref-mirror so `handleChange` can compare the debounced buffer against the
  // latest serialized content without depending on `currentContent` in its
  // deps (which would tear down the shared debounce timer on every keystroke
  // that touches the workflow tree).
  const currentContentRef = useRef(currentContent);
  currentContentRef.current = currentContent;

  // Handle content changes from the editor
  const handleChange = useCallback(
    (value: string) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Record divergence immediately. This closes the 500 ms window where
      // another editor could mutate the workflow while this visible buffer had
      // unapplied text. The debounced parse below replaces this provisional
      // state with the final syntax/schema result.
      setCodeBufferState({
        dirty: value !== currentContentRef.current,
        parseError: null,
      });

      // Debounce the parse and update
      debounceTimerRef.current = setTimeout(() => {
        const updateFn =
          activeFile === 'workflow.json' ? updateWorkflowFromJson : updateConnectionsFromJson;
        const result = updateFn(value);
        const parseError = result.success ? null : result.error ?? 'Invalid JSON';
        setParseError(parseError);

        // Track buffer divergence explicitly so Save reacts even when the
        // buffer fails to parse. On success, clear the buffer-divergence flag
        // because the store has already been updated from the parsed JSON.
        // Note: workflow.json is normalized/pretty-printed; connections.json
        // currently preserves the user's formatting.
        // On parse failure the tree is unchanged and any divergence is unsaved intent.
        const diverges = value !== currentContentRef.current;
        setCodeBufferState({
          dirty: !result.success && diverges,
          parseError,
        });
      }, 500);
    },
    [activeFile, updateWorkflowFromJson, updateConnectionsFromJson, setCodeBufferState]
  );

  // Initialize CodeMirror
  const { containerRef, setValue, updateTheme, updateLinter, updateReadOnly, highlightNode } = useCodeMirror({
    initialValue: currentContent,
    onChange: handleChange,
    theme,
    activeFile,
    readOnly: isReadOnly,
  });

  // Update theme when it changes
  useEffect(() => {
    updateTheme(theme);
  }, [theme, updateTheme]);

  // Toggle read-only when draft/published mode flips
  useEffect(() => {
    updateReadOnly(isReadOnly);
  }, [isReadOnly, updateReadOnly]);

  // Sync content when file changes or external update occurs
  useEffect(() => {
    const fileChanged = previousActiveFileRef.current !== activeFile;

    if (fileChanged) {
      // File switched - update content and linter. Also clear any lingering
      // buffer-divergence state from the previous file so Save doesn't stay
      // stuck on a parse error the user has already navigated away from.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setValue(currentContent);
      updateLinter(activeFile);
      setParseError(null);
      setCodeBufferState({ dirty: false, parseError: null });
      previousActiveFileRef.current = activeFile;
      previousContentRef.current = currentContent;
    } else if (currentContent !== previousContentRef.current) {
      // Content changed externally (e.g., from canvas interaction)
      setValue(currentContent);
      previousContentRef.current = currentContent;
    }
  }, [activeFile, currentContent, setValue, updateLinter, setCodeBufferState]);

  // Track the last selected node to avoid re-highlighting on every render
  const lastSelectedNodeRef = useRef<SelectedNode | null>(null);

  // Highlight selected node in the code editor
  useEffect(() => {
    // Only highlight when viewing workflow.json (not connections.json)
    if (activeFile !== 'workflow.json') {
      // Clear highlight when not viewing workflow.json
      highlightNode(null);
      return;
    }

    // Check if selection actually changed
    const selectionChanged =
      selectedNode?.id !== lastSelectedNodeRef.current?.id ||
      selectedNode?.name !== lastSelectedNodeRef.current?.name;

    if (!selectionChanged) return;

    lastSelectedNodeRef.current = selectedNode;

    if (selectedNode && workflowJson) {
      const range = findNodeRange(workflowJson, selectedNode);
      if (range) {
        // Use requestAnimationFrame to ensure the editor has finished updating
        requestAnimationFrame(() => {
          highlightNode(range);
        });
      }
    } else {
      // Clear highlight when no node is selected
      highlightNode(null);
    }
  }, [selectedNode, workflowJson, activeFile, highlightNode]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      {!hideHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <TabList
              className={styles.fileTabs}
              selectedValue={activeFile}
              onTabSelect={handleFileTabChange}
              size="small"
            >
              <Tab value="workflow.json">workflow.json</Tab>
              <Tab value="connections.json" disabled={!connections}>
                connections.json
              </Tab>
            </TabList>
          </div>
          {parseError && <Text className={styles.errorBadge}>{parseError}</Text>}
          {!parseError && isReadOnly && (
            <Text className={styles.readOnlyBadge}>
              Read-only — switch to draft mode to edit
            </Text>
          )}
        </div>
      )}
      <div className={styles.editorContainer}>
        <div ref={containerRef} className={styles.editorWrapper} />
      </div>
    </div>
  );
};
